import { useCallback, useMemo, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { KeyboardArrowUpIcon, KeyboardArrowDownIcon } from "@l2r-front/l2r-icons"
import { useNetworksContext } from "@l2r-front/l2r-networks"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { useTheme, useMediaQuery, MenuItem, Typography } from "@l2r-front/l2r-ui"

import { NetworkSubMenu } from "../../components/NetworkSubMenu"
import { I18N_NAMESPACE } from "../../constants/i18n"

import * as Styled from "./NetworkSelector.styled"

export function NetworkSelector(props) {
    const {
        className,
    } = props

    const [tempSelectedNetwork, setTempSelectedNetwork] = useState(null)
    const [tempSelectedSubNetworks, setTempSelectedSubNetworks] = useState({})

    const [networksState, networksDispatch] = useNetworksContext()
    const { allNetworks, selectedNetwork, selectedSubNetworkSlugs } = networksState
    const { swapToNetwork } = networksDispatch
    const { setAlert } = useAlertsDispatchContext()
    const theme = useTheme()
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"))
    const { t } = useTranslation(I18N_NAMESPACE)

    const [anchorEl, setAnchorEl] = useState(null)
    const open = Boolean(anchorEl)

    const parentNetworks = useMemo(() => {
        if (!allNetworks) {
            return []
        }
        return allNetworks.filter(n => !n.parent)
    }, [allNetworks])

    const canOpenNetworksList = useMemo(() => {
        return !(parentNetworks.length === 1 && parentNetworks[0]?.children?.length <= 1)
    }, [parentNetworks])

    const hasOnlyNetworkWithSubNetworks = useMemo(() => {
        return parentNetworks.length === 1 && parentNetworks[0]?.children?.length > 0
    }, [parentNetworks])

    const displayedNetworkName = useMemo(() => {
        if (!selectedNetwork?.role && selectedNetwork?.["children"]?.length === 1) {
            return selectedNetwork.children[0].name
        } else {
            return selectedNetwork?.name
        }
    }, [selectedNetwork])

    const subnetworksBadgeCount = useMemo(() => {
        if (selectedSubNetworkSlugs.length === selectedNetwork?.children?.length) {
            return 0
        }
        return selectedSubNetworkSlugs.length
    }, [selectedNetwork, selectedSubNetworkSlugs])

    const changeSelectedNetworkSlug = useCallback((event, newNetwork) => {
        if (newNetwork && newNetwork.slug !== selectedNetwork.slug) {
            swapToNetwork(newNetwork.slug)
            setTempSelectedNetwork(null)
        }
    }, [selectedNetwork, swapToNetwork])

    const handleButtonClick = useCallback((e) => {
        if (!canOpenNetworksList) {
            return
        }
        setAnchorEl(e.currentTarget)
    }, [canOpenNetworksList])

    const changeNetworkIfPermitted = useCallback((network, subnetworks) => {
        if (network && !subnetworks?.length) {
            setAlert({
                alertTitle: t(I18N_NAMESPACE, "containers.networkSelector.noNetworkWarning.label"),
                confirmText: t(I18N_NAMESPACE, "containers.networkSelector.noNetworkWarning.cancel"),
                onlyConfirm: true,
                severity: "warning",
            })
            setTempSelectedSubNetworks(value => {
                return {
                    ...value,
                    [network]: null,
                }
            })
        } else if (network) {
            swapToNetwork(network, subnetworks)
            setTempSelectedNetwork(null)
        }
    }, [
        swapToNetwork,
        setAlert,
        t,
    ])

    const handleMenuClose = useCallback((checkTempSelection = false) => {
        setAnchorEl(null)

        if (checkTempSelection && tempSelectedNetwork) {
            changeNetworkIfPermitted(tempSelectedNetwork, tempSelectedSubNetworks[tempSelectedNetwork] || null)
        }
        setTempSelectedNetwork(null)
        setTempSelectedSubNetworks({})
    }, [
        tempSelectedNetwork,
        tempSelectedSubNetworks,
        changeNetworkIfPermitted,
    ])

    const handleSubNetworksChange = useCallback((network, subnetworks) => {
        setTempSelectedNetwork(network.slug)
        setTempSelectedSubNetworks(value => {
            return {
                ...value,
                [network.slug]: subnetworks,
            }
        })
    }, [])

    const getActiveSubNetworks = useCallback((network) => {
        if (!tempSelectedSubNetworks[network.slug]) {
            if (network.slug === selectedNetwork.slug) {
                return selectedSubNetworkSlugs
            } else {
                return network.children.map((sub) => sub.slug)
            }
        }
        return tempSelectedSubNetworks[network.slug]
    }, [
        selectedNetwork,
        selectedSubNetworkSlugs,
        tempSelectedSubNetworks,
    ])

    const handleSubNetworksMenuClose = useCallback((network, subNetworks) => {
        setAnchorEl(null)
        changeNetworkIfPermitted(network.slug, subNetworks)
    }, [changeNetworkIfPermitted])

    const networksList = useMemo(() => {
        if (!parentNetworks) {
            return null
        }

        return parentNetworks.map(network => (
            !network.children.length || network.children.length === 1 ?
                <MenuItem
                    key={network.slug}
                    selected={network.slug === selectedNetwork.slug}
                    onClick={(event) => {
                        changeSelectedNetworkSlug(event, network)
                        handleMenuClose()
                    }} >
                    <Typography>{network.children.length === 1 ? network.children[0].name : network.name}</Typography>
                </MenuItem>
                : <NetworkSubMenu
                    key={network.slug}
                    network={network}
                    selected={network.slug === selectedNetwork.slug}
                    activeSubNetworks={getActiveSubNetworks(network)}
                    onChange={(subnetworks) => handleSubNetworksChange(network, subnetworks)}
                    onClick={() => {
                        changeNetworkIfPermitted(network.slug, getActiveSubNetworks(network))
                        handleMenuClose()
                    }}
                />
        ))
    }, [
        parentNetworks,
        selectedNetwork,
        changeSelectedNetworkSlug,
        handleMenuClose,
        getActiveSubNetworks,
        handleSubNetworksChange,
        changeNetworkIfPermitted,
    ])

    if (!networksList?.length) {
        return null
    }

    return (
        <>
            <Styled.Button className={className} large={isLargeScreen} onClick={handleButtonClick} endIcon={canOpenNetworksList ? open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon /> : null}>
                <Styled.NetworkNameWrapper>
                    {displayedNetworkName}
                </Styled.NetworkNameWrapper>
                {subnetworksBadgeCount > 0 &&
                    <Styled.Badge>
                        <Typography variant="Desktop & Mobile/Body/Label & Helper text">{subnetworksBadgeCount}</Typography>
                    </Styled.Badge>
                }
            </Styled.Button>
            {
                hasOnlyNetworkWithSubNetworks ?
                    <Styled.SubNetworksSelector
                        network={parentNetworks[0]}
                        activeSubNetworks={getActiveSubNetworks(parentNetworks[0])}
                        anchorEl={anchorEl}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        open={Boolean(anchorEl)}
                        onChange={(subnetworks) => handleSubNetworksChange(parentNetworks[0], subnetworks)}
                        onClose={handleSubNetworksMenuClose}
                    /> : <Styled.Menu anchorEl={anchorEl} open={open} onClose={() => handleMenuClose(true)}>
                        {networksList}
                    </Styled.Menu>
            }
        </>
    )

}